import {
  deriveGatewayShellState,
  type GatewayShellState,
  type UiErrorLike,
} from "@lifter/hub-ui/browser-shared";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import {
  type CurrentOrgData,
  HubApiError,
  useCurrentOrgQuery,
  useGatewayEventsSse,
  useGatewayStatusQuery,
} from "../api";

interface PortalOrgContextValue {
  current: CurrentOrgData;
  gateway: GatewayShellState;
  gatewayOnline: boolean;
  canEditBranding: boolean;
  canCreateSpecialists: boolean;
  /** Application-side Administrator authority for organisation operations. */
  isAdmin: boolean;
  bypassConfirmationsEnabled: boolean;
}

const PortalOrgContext = createContext<PortalOrgContextValue | null>(null);

export function PortalOrgProvider({
  current,
  children,
}: {
  current: CurrentOrgData;
  children: ReactNode;
}) {
  const gatewayEvents = useGatewayEventsSse();
  const gatewayStatus = useGatewayStatusQuery({ polling: !gatewayEvents.connected });
  const gateway = useMemo(
    () =>
      deriveGatewayShellState({
        pending: gatewayStatus.isPending,
        data: gatewayStatus.data,
        error: toUiErrorLike(gatewayStatus.error),
      }),
    [gatewayStatus.data, gatewayStatus.error, gatewayStatus.isPending],
  );

  const value = useMemo(
    () => ({
      current,
      gateway,
      gatewayOnline: gateway.online,
      canEditBranding: current.membership.canEditBranding,
      canCreateSpecialists: current.membership.canCreateSpecialists,
      isAdmin: current.membership.isAdmin,
      bypassConfirmationsEnabled: current.security?.bypassConfirmationsEnabled === true,
    }),
    [current, gateway],
  );

  return <PortalOrgContext.Provider value={value}>{children}</PortalOrgContext.Provider>;
}

export function usePortalOrg(): PortalOrgContextValue {
  const value = useContext(PortalOrgContext);
  if (!value) {
    throw new Error("PortalOrgProvider is missing");
  }
  return value;
}

export { useCurrentOrgQuery };

function toUiErrorLike(error: unknown): UiErrorLike | undefined {
  if (!error) return undefined;
  if (error instanceof HubApiError) {
    return { status: error.status, errorCode: error.errorCode };
  }
  return {};
}
